import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnDestroy,
	OnInit, ViewChild
} from '@angular/core';
import { helpUrlsLookup } from 'go-modules/help-urls/help-urls.constant';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import type {
	ActivityReport,
	MarkerCount,
	SessionReport
} from '@go-modules/interfaces/activity-summary-report';
import { Marker } from '@go-modules/interfaces/markers/marker';

import {
	BehaviorSubject,
	Observable,
	ReplaySubject,
	Subject,
	of,
	tap,
	defer
} from 'rxjs';
import {
	catchError,
	finalize,
	map,
	shareReplay,
	takeUntil
} from 'rxjs/operators';
import { AsyncPipe, DecimalPipe, TitleCasePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GoMaterialModule } from '@go-modules/go-material.module';
import { FormatTimeModule } from '@go-modules/pipes/format-time/format-time.module';
import { NgxZeroStateModule } from '@go-modules/components/zero-state/zero-state.module';
import { MarkerDisplayModule } from '@go-modules/components/marker-display/marker-display.module';
import { CdkTableModule } from '@angular/cdk/table';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { MatTable } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { GoCdkPopoverDirective } from '@go-modules/directives/go-cdk-popover/go-cdk-popover.directive';

export enum LoadingState {
	LOADING= 'loading',
	LOADED= 'loaded',
	ERROR= 'error',
}

@Component({
	selector: 'activity-report',
	imports: [
		AsyncPipe,
		TranslateModule,
		GoMaterialModule,
		FormatTimeModule,
		NgxZeroStateModule,
		MarkerDisplayModule,
		CdkTableModule,
		CdkAccordionModule,
		MatIconModule,
		TitleCasePipe,
		GoCdkPopoverDirective,
		DecimalPipe
	],
	templateUrl: './activity-report.component.html',
	styleUrls: ['./activity-report.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityReportComponent implements OnInit, OnDestroy {
	@Input() public activityId!: number;
	@Input() public groupId!: number;
	@ViewChild(MatTable, {static: false}) private table?: MatTable<ActivityReport>;

	public helpUrls = helpUrlsLookup;
	public LoadingState = LoadingState;
	public readonly loadedState$ = new BehaviorSubject<LoadingState>(
		LoadingState.LOADING
	);
	public readonly displayedColumns = [
		'expander', 'user', 'role', 'videosPresented', 'videosReviewed', 'commentsWritten', 'avgCommentLen', 'markers', 'avgScore'
	] as const;
	public lastExpandedRow: number | null = null;

	// defer until onInit subscribe so that @Input params are defined when calling getSummaryReport
	public datasource$ = defer(() => this.activityService.getSummaryReport(this.activityId, this.groupId))
		.pipe(
			// transform markerCount to markers
			map((data) => data.map((item) => {
				const transformedMarkers: Marker[] = item.marker_counts.map((markerCount: MarkerCount) => ({
					tag_id: markerCount.tag_id,
					tag_name: markerCount.tag_name,
					tag_color: markerCount.tag_color,
					tag_abbreviation: markerCount.tag_abbreviation,
					count: markerCount.count,
					created_at: '',
					deleted_at: null,
					deleted_by: null
				}));
				return { ...item, transformedMarkers };
			})),
			map((list) => {
				return [...list].sort((a, b) => a.first_name.localeCompare(b.first_name));
			}),
			tap(() => {
				this.loadedState$.next(LoadingState.LOADED);
			}),
			catchError(() => {
				this.loadedState$.next(LoadingState.ERROR);
				return of([] as ActivityReport[]);
			}),
			shareReplay(1)
		);
	public readonly expandedIds = new Set<number>();

	public readonly rowLoadingIds$ = new BehaviorSubject<Set<number>>(new Set());
	private readonly userReportsCache = new Map<number, ReplaySubject<SessionReport[]>>();

	private readonly destroy$ = new Subject<void>();
	constructor (
		private readonly activityService: NgxActivityService,
		private readonly sessionService: NgxSessionService
	) {
	}

	public ngOnInit (): void {
		this.datasource$.pipe(takeUntil(this.destroy$)).subscribe();
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.userReportsCache.forEach((s) => s.complete());
		this.userReportsCache.clear();
	}

	public userReport$ (userId: number): Observable<SessionReport[] | undefined> {
		const subj = this.userReportsCache.get(userId);
		return subj ? subj.asObservable() : of([]);
	}

	public toggleRow (userId: number): void {
		if (this.expandedIds.has(userId)) {
			this.expandedIds.delete(userId);
			if (this.lastExpandedRow === userId) {
				this.lastExpandedRow = null;
			}
		} else {
			this.expandedIds.add(userId);
			this.lastExpandedRow = userId;
			this.getUserDetails(userId);
		}
		// because the expanded table details is not part of the parent datasource, manually trigger a re-render
		this.table.renderRows();
	}

	public isExpandedRow = (_x: number, row: ActivityReport) => this.expandedIds.has(row.user_id);

	public dashIfNull (v: number | null | undefined): string | number {
		return v == null ? '-' : v;
	}

	public commentsAsc (s: SessionReport) {
		// sorted by time asc
		const arr = (s.comments).slice();
		arr.sort((a, b) => {
			const ta = a.time ?? Number.POSITIVE_INFINITY;
			const tb = b.time ?? Number.POSITIVE_INFINITY;
			return ta - tb;
		});
		return arr;
	}

	public isRowLoading (
		userId: number,
		loadingSet: Set<number> | null
	): boolean {
		return !!loadingSet?.has(userId);
	}

	public sumMarkerCounts (markerCounts: MarkerCount[]): number {
		return markerCounts.reduce((acc, curr) => acc + curr.count, 0);
	}

	private getUserDetails (userId: number): void {
		if (this.userReportsCache.has(userId)) return;

		// ReplaySubject instead of BehaviorSubject. we don't want an initial value until resolved
		const subj = new ReplaySubject<SessionReport[]>(1);
		this.userReportsCache.set(userId, subj);

		this.setRowLoading(userId, true);
		this.sessionService.getSummaryReport(this.groupId, userId, this.activityId)
			.pipe(
				// transform markerCount to markers
				map((data) => data.map((item) => {
					const transformedMarkers: Marker[] = item.marker_counts.map((markerCount: MarkerCount) => ({
						tag_id: markerCount.tag_id,
						tag_name: markerCount.tag_name,
						tag_color: markerCount.tag_color,
						tag_abbreviation: markerCount.tag_abbreviation,
						count: markerCount.count,
						created_at: '',
						deleted_at: null,
						deleted_by: null
					}));
					return { ...item, transformedMarkers };
				})),
				finalize(() => this.setRowLoading(userId, false)),
				takeUntil(this.destroy$)
			)
			.subscribe({
				// NOTE: Remove as SessionReport[] when cleaning up NEW_ACTIVITY_REPORT flag
				next: (data) => subj.next(data as SessionReport[]),
				error: () => subj.next([])
			});
	}

	private setRowLoading (userId: number, isLoading: boolean) {
		const set = new Set(this.rowLoadingIds$.value);
		if (isLoading) {
			set.add(userId);
		} else {
			set.delete(userId);
		}

		this.rowLoadingIds$.next(set);
	}
}
